import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Number formatting helpers used across dashboards
export function safeNumber(n: unknown, fallback = 0): number {
  const v = Number(n);
  return Number.isFinite(v) ? v : fallback;
}

export function isMissing(n: unknown): boolean {
  return n === null || n === undefined || (typeof n === "number" && !Number.isFinite(n));
}

export function formatNumber(
  n: unknown,
  opts: { compact?: boolean; decimals?: number } = {}
): string {
  if (isMissing(n)) return "—";
  const value = safeNumber(n, 0);
  const { compact = true, decimals = 0 } = opts;
  try {
    return new Intl.NumberFormat(undefined, {
      notation: compact ? "compact" : undefined,
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals,
    } as Intl.NumberFormatOptions).format(value);
  } catch {
    return String(value.toFixed(decimals));
  }
}

export function formatPercent(
  n: unknown,
  opts: { decimals?: number; showSign?: boolean } = {}
): string {
  if (isMissing(n)) return "—";
  const value = safeNumber(n, 0);
  const { decimals = 1, showSign = false } = opts;
  const sign = showSign && value !== 0 ? (value > 0 ? "+" : "") : "";
  return `${sign}${value.toFixed(decimals)}%`;
}

export function formatCurrency(
  n: unknown,
  currency = "USD",
  opts: { decimals?: number } = {}
): string {
  if (isMissing(n)) return "—";
  const value = safeNumber(n, 0);
  const { decimals = 0 } = opts;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals,
    }).format(value);
  } catch {
    return `$${value.toFixed(decimals)}`;
  }
}

// Tone helpers to style positive/negative/zero/missing values consistently
export function valueTone(n: unknown): string {
  if (isMissing(n)) return "text-muted-foreground";
  const v = safeNumber(n, 0);
  if (v > 0) return "text-green-600";
  if (v < 0) return "text-red-600";
  return "text-muted-foreground";
}

export function bgTone(n: unknown): string {
  if (isMissing(n)) return "bg-muted";
  const v = safeNumber(n, 0);
  if (v > 0) return "bg-green-50";
  if (v < 0) return "bg-red-50";
  return "bg-muted";
}

// Axis tick formatter for large numbers (K/M)
export function formatAxisTick(n: number): string {
  const v = safeNumber(n, 0);
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
}

// Simple unicode trend arrows for deltas
export function trendArrow(n: unknown): string {
  if (isMissing(n)) return "–";
  const v = safeNumber(n, 0);
  if (v > 0) return "▲";
  if (v < 0) return "▼";
  return "–";
}
